/**
 * Sayify API Module
 * 
 * API methods for Sayify.pro using existing UseAPI utility
 * Uses /api/ endpoints for app (token auth)
 */
import UseAPI from './api/UseAPI';
import api_config from './api/common';

const API_BASE = api_config.BASE_BACKEND_ENDPOINT;

// Helper to get tenant host header for public API calls.
// The workspace header disambiguates slugs on hosts that resolve no tenant
// (localhost and platform hosts like app.sayify.pro) — link slugs are only
// unique per workspace. localStorage is per-origin, so on tenant subdomains
// and custom domains there is no stored workspace and the header is omitted.
const getTenantHostHeader = () => {
    const headers = { 'X-Tenant-Host': window.location.hostname };
    let workspace = null;
    try {
        workspace = localStorage.getItem('current_workspace_uuid');
    } catch (e) {
        // Storage unavailable (e.g. blocked in embedded contexts) — skip.
    }
    if (workspace) {
        headers['X-Workspace-Uuid'] = workspace;
    }
    return headers;
};

// ==========================================
// FEEDBACK LINKS (App - Token Auth)
// ==========================================

export const listLinks = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) {
        queryParams.workspace = workspace;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/links/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getLink = async (signal, linkUuid) => {
    const url = `${API_BASE}api/links/${linkUuid}/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createLink = async (signal, data) => {
    const url = `${API_BASE}api/links/`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateLink = async (signal, linkUuid, data) => {
    const url = `${API_BASE}api/links/${linkUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const updateLinkBranding = async (signal, linkUuid, data) => {
    const url = `${API_BASE}api/links/${linkUuid}/branding/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const updateLinkLayout = async (signal, linkUuid, data) => {
    const url = `${API_BASE}api/links/${linkUuid}/layout/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const getWorkflowDraft = async (signal, linkUuid) => {
    const url = `${API_BASE}api/links/${linkUuid}/draft/`;
    return UseAPI(signal, url, 'GET', null);
};

export const saveWorkflowDraft = async (signal, linkUuid, schemaJson, options = {}) => {
    const url = `${API_BASE}api/links/${linkUuid}/draft/`;
    return UseAPI(signal, url, 'PUT', { schema_json: schemaJson, ...options });
};

export const publishWorkflowDraft = async (signal, linkUuid) => {
    const url = `${API_BASE}api/links/${linkUuid}/publish/`;
    return UseAPI(signal, url, 'POST', {});
};

export const deleteInboxCase = async (signal, caseUuid) => {
    const url = `${API_BASE}api/inbox/${caseUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

// ==========================================
// SAVED VIEWS (App - Token Auth)
// ==========================================

export const listSavedViews = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryString = new URLSearchParams(workspace ? { workspace } : {}).toString();
    const url = `${API_BASE}api/inbox/views/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const createSavedView = async (signal, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const payload = { ...data, workspace };
    const url = `${API_BASE}api/inbox/views/`;
    return UseAPI(signal, url, 'POST', payload);
};

export const updateSavedView = async (signal, viewUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const payload = { ...data, workspace };
    const url = `${API_BASE}api/inbox/views/${viewUuid}/?workspace=${workspace}`;
    return UseAPI(signal, url, 'PATCH', payload);
};

export const deleteSavedView = async (signal, viewUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/inbox/views/${viewUuid}/?workspace=${workspace}`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const deleteLink = async (signal, linkUuid) => {
    const url = `${API_BASE}api/links/${linkUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const duplicateLink = async (signal, linkUuid) => {
    const url = `${API_BASE}api/links/${linkUuid}/duplicate/`;
    return UseAPI(signal, url, 'POST', null);
};

export const checkLinkSlugAvailability = async (signal, slug, excludeUuid = null) => {
    const params = new URLSearchParams({ slug });
    const workspace = localStorage.getItem('current_workspace_uuid');
    if (workspace) params.append('workspace', workspace);
    if (excludeUuid) params.append('exclude', excludeUuid);
    const url = `${API_BASE}api/links/check-slug/?${params.toString()}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getSmartBlockPresets = async (signal) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/links/smart-block-presets/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

/**
 * Translate/clean a raw transcript for the AI Context field.
 * The frontend calls the Lambda directly for transcription,
 * then sends the raw transcript here for English translation + cleanup.
 * @param {AbortSignal} signal
 * @param {string} linkUuid - Link UUID
 * @param {string} transcript - Raw transcribed text from Lambda
 * @returns {{ text: string, original_language: string, original_text: string|null }}
 */
export const translateContext = async (signal, linkUuid, transcript, workspaceUuid = null) => {
    let url;
    if (linkUuid) {
        url = `${API_BASE}api/links/${linkUuid}/transcribe-context/`;
    } else if (workspaceUuid) {
        url = `${API_BASE}api/workspaces/${workspaceUuid}/transcribe-context/`;
    } else {
        throw new Error('Either linkUuid or workspaceUuid must be provided');
    }
    return UseAPI(signal, url, 'POST', { transcript });
};

/**
 * Get a presigned S3 upload URL for context audio recording.
 * For audio uploads, also returns Lambda transcription credentials:
 *   { transcription: { lambda_url, session_id, session_token } }
 * @param {AbortSignal} signal
 * @param {string} linkUuid - Link UUID
 * @param {string} contentType - Audio MIME type
 * @param {string} workspaceUuid - Workspace UUID (fallback if link isn't saved)
 * @returns {{ upload_url: string, s3_key: string, transcription?: object }}
 */
export const getContextUploadUrl = async (signal, linkUuid, contentType, workspaceUuid = null, options = {}) => {
    let url;
    if (linkUuid) {
        url = `${API_BASE}api/links/${linkUuid}/upload-question-media/`;
    } else if (workspaceUuid) {
        url = `${API_BASE}api/workspaces/${workspaceUuid}/upload-media/`;
    } else {
        throw new Error('Either linkUuid or workspaceUuid must be provided');
    }
    const body = {
        content_type: contentType || 'audio/webm',
        file_name: options.file_name || `context-dictation-${Date.now()}.webm`,
        upload_type: options.upload_type || 'media',
    };
    if (options.file_size) body.file_size = options.file_size;
    return UseAPI(signal, url, 'POST', body);
};

/**
 * Get a presigned S3 upload URL for a welcome video greeting.
 * The backend generates an S3 key under the welcome-videos/ prefix
 * which triggers the Lambda thumbnail generator via S3 event notification.
 *
 * @param {AbortSignal} signal
 * @param {string} linkUuid - Link UUID (null for new forms)
 * @param {string} fileName - Original file name
 * @param {string} contentType - Video MIME type
 * @param {string} workspaceUuid - Workspace UUID fallback (for new forms)
 * @returns {{ upload_url: string, s3_key: string, media_url: string }}
 */
export const getVideoGreetingUploadUrl = async (signal, linkUuid, fileName, contentType, workspaceUuid = null) => {
    let url;
    if (linkUuid) {
        url = `${API_BASE}api/links/${linkUuid}/upload-question-media/`;
    } else if (workspaceUuid) {
        url = `${API_BASE}api/workspaces/${workspaceUuid}/upload-media/`;
    } else {
        throw new Error('Either linkUuid or workspaceUuid must be provided');
    }
    return UseAPI(signal, url, 'POST', {
        content_type: contentType,
        file_name: fileName,
        upload_type: 'welcome_video',
    });
};

/**
 * Confirm a video upload by writing a request manifest to S3.
 * This triggers the sayify_create_video_thumb Lambda to generate
 * thumbnails and GIF previews.
 *
 * Call this AFTER the presigned URL upload to S3 completes.
 *
 * @param {AbortSignal} signal
 * @param {string} linkUuid - Link UUID (null for workspace-level uploads)
 * @param {string} s3Key - The S3 key returned from getVideoGreetingUploadUrl
 * @param {string} contentType - Video MIME type
 * @param {string} workspaceUuid - Workspace UUID (fallback if no link)
 * @returns {{ success: boolean, manifest_key: string, s3_key: string }}
 */
export const confirmVideoUpload = async (signal, linkUuid, s3Key, contentType, workspaceUuid = null) => {
    let url;
    if (linkUuid) {
        url = `${API_BASE}api/links/${linkUuid}/confirm-video-upload/`;
    } else if (workspaceUuid) {
        url = `${API_BASE}api/workspaces/${workspaceUuid}/confirm-video-upload/`;
    } else {
        throw new Error('Either linkUuid or workspaceUuid must be provided');
    }
    return UseAPI(signal, url, 'POST', {
        s3_key: s3Key,
        content_type: contentType,
        link_uuid: linkUuid || '',
    });
};

export const getIntakeTemplates = async (signal) => {
    const url = `${API_BASE}api/intake-templates/`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// FORM TEMPLATES (App - Token Auth)
// ==========================================

export const getTemplates = async (signal, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE}api/templates/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getTemplateDetail = async (signal, slug) => {
    const url = `${API_BASE}api/templates/${slug}/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createFromTemplate = async (signal, slug, data = {}) => {
    // The workspace is pulled from the token automatically by CustomTokenAuthentication
    const url = `${API_BASE}api/templates/${slug}/create-link/`;
    return UseAPI(signal, url, 'POST', data);
};

// ==========================================
// ANALYTICS (App - Token Auth)
// ==========================================

/**
 * Get aggregated analytics for responses
 * @param {AbortSignal} signal 
 * @param {Object} params - Query parameters:
 *   - workspace: Workspace UUID (optional)
 *   - link: Filter by link UUID (optional)
 */
export const getAnalytics = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) {
        queryParams.workspace = workspace;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/analytics/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getIntegrationStats = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };  // Include all params, not just workspace
    if (workspace) {
        queryParams.workspace = workspace;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/integrations/stats/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const exportResponses = async (signal, exportFormat, filters = {}) => {
    const url = `${API_BASE}api/responses/export/`;
    const payload = { export_format: exportFormat, ...filters };
    
    // We use POST so that we can send a potentially large list of uuids in the request body
    const data = await UseAPI(signal, url, 'POST', payload);

    if (data?.httpStatusCode && data.httpStatusCode !== 200) {
        throw new Error(data.message || data.error || 'Export failed');
    }

    // Decode base64 content from JSON response
    const byteChars = atob(data.content);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
        byteArray[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: data.content_type });
    const filename = data.filename || `export.${exportFormat}`;

    return { blob, filename };
};

// ==========================================
// RESPONSES (App - Token Auth)
// ==========================================

/**
 * List all responses across all links in workspace (global)
 * @param {AbortSignal} signal 
 * @param {Object} params - Query parameters:
 *   - workspace: Workspace UUID (optional)
 *   - search: Search in email/transcript
 *   - link: Filter by link UUID
 *   - date_from: ISO date string
 *   - date_to: ISO date string
 *   - sentiment: positive|neutral|negative
 *   - has_email: true|false
 *   - duplicate_flagged: true|false
 *   - ordering: Sort field (e.g., '-submitted_at')
 *   - page: Page number
 *   - page_size: Results per page (default 20)
 */
export const listAllResponses = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) {
        queryParams.workspace = workspace;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/responses/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

/**
 * List responses for a specific link
 * @param {AbortSignal} signal 
 * @param {string} linkUuid - Link UUID
 * @param {Object} params - Same query parameters as listAllResponses
 */
export const listResponses = async (signal, linkUuid, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) {
        queryParams.workspace = workspace;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/links/${linkUuid}/responses/${queryString ? '?' + queryString : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

/**
 * Get single response detail (global - works without link UUID)
 * @param {AbortSignal} signal 
 * @param {string} responseUuid - Response UUID
 */
export const getResponseDetail = async (signal, responseUuid, params = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) {
        queryParams.workspace = workspace;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/responses/${responseUuid}/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

/**
 * Get response detail for specific link (legacy - for backward compatibility)
 * @param {AbortSignal} signal 
 * @param {string} linkUuid - Link UUID
 * @param {string} responseUuid - Response UUID
 */
export const getResponse = async (signal, linkUuid, responseUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/links/${linkUuid}/responses/${responseUuid}/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

/**
 * Export responses to CSV
 * @param {AbortSignal} signal 
 * @param {Object} params - Same filter parameters as listAllResponses
 */
export const exportResponsesCSV = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { export_format: 'csv', ...params };
    if (workspace) {
        queryParams.workspace = workspace;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/responses/export/?${queryString}`;
    // Note: Export will be implemented in backend next
    return UseAPI(signal, url, 'GET', null);
};

/**
 * Export responses to JSON
 * @param {AbortSignal} signal 
 * @param {Object} params - Same filter parameters as listAllResponses
 */
export const exportResponsesJSON = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { export_format: 'json', ...params };
    if (workspace) {
        queryParams.workspace = workspace;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/responses/export/?${queryString}`;
    // Note: Export will be implemented in backend next
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// PUBLIC ENDPOINTS (No Auth - External Respondents)
// ==========================================

export const getPublicLink = async (signal, slug, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const url = `${API_BASE}api/public/links/${slug}/${qs ? `?${qs}` : ''}`;
    return UseAPI(signal, url, 'GET', null, false, getTenantHostHeader());
};

export const createPaymentIntent = async (signal, data) => {
    const url = `${API_BASE}api/public/payments/create-intent/`;
    return UseAPI(signal, url, 'POST', data, false, getTenantHostHeader());
};

// ==========================================
// APP ENDPOINTS (Session Auth - Internal App)
// ==========================================

export const startResponseSession = async (signal, data) => {
    const url = `${API_BASE}api/public/responses/start/`;
    return UseAPI(signal, url, 'POST', data, false, getTenantHostHeader());
};

export const resumeResponseSession = async (signal, data) => {
    const url = `${API_BASE}api/public/responses/resume/`;
    return UseAPI(signal, url, 'POST', data, false, getTenantHostHeader());
};

export const submitAnswer = async (signal, sessionId, data, sessionToken = null) => {
    const url = `${API_BASE}api/public/responses/${sessionId}/answers/`;
    const headers = { ...getTenantHostHeader(), ...(sessionToken ? { 'X-Session-Token': sessionToken } : {}) };
    return UseAPI(signal, url, 'POST', data, false, headers);
};

export const submitSession = async (signal, sessionId, sessionToken = null) => {
    const url = `${API_BASE}api/public/responses/${sessionId}/submit/`;
    const headers = { ...getTenantHostHeader(), ...(sessionToken ? { 'X-Session-Token': sessionToken } : {}) };
    return UseAPI(signal, url, 'POST', null, false, headers);
};

export const getVisibleQuestions = async (signal, sessionId, sessionToken = null) => {
    const url = `${API_BASE}api/public/responses/${sessionId}/visible-questions/`;
    const headers = { ...getTenantHostHeader(), ...(sessionToken ? { 'X-Session-Token': sessionToken } : {}) };
    return UseAPI(signal, url, 'POST', null, false, headers);
};

export const getAnswerEvaluationStatus = async (signal, sessionId, answerUuid, sessionToken = null) => {
    const url = `${API_BASE}api/public/responses/${sessionId}/answers/${answerUuid}/status/`;
    const headers = { ...getTenantHostHeader(), ...(sessionToken ? { 'X-Session-Token': sessionToken } : {}) };
    return UseAPI(signal, url, 'GET', null, false, headers);
};

export const reportConfigError = async (signal, sessionId, data, sessionToken = null) => {
    const url = `${API_BASE}api/public/responses/${sessionId}/report-config-error/`;
    const headers = { ...getTenantHostHeader(), ...(sessionToken ? { 'X-Session-Token': sessionToken } : {}) };
    return UseAPI(signal, url, 'POST', data, false, headers);
};

// ==========================================
// WORKSPACE MANAGEMENT (App - Token Auth)
// ==========================================

export const listWorkspaces = async (signal) => {
    const url = `${API_BASE}api/workspaces/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getWorkspace = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createWorkspace = async (signal, data) => {
    const url = `${API_BASE}api/workspaces/`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateWorkspace = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteWorkspace = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const switchWorkspace = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/switch/`;
    return UseAPI(signal, url, 'POST', null);
};

export const listWorkspaceMembers = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/members/`;
    return UseAPI(signal, url, 'GET', null);
};

export const inviteMember = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/invite/`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateMemberRole = async (signal, workspaceUuid, memberUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/members/${memberUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const removeMember = async (signal, workspaceUuid, memberUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/members/${memberUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const leaveWorkspace = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/leave/`;
    return UseAPI(signal, url, 'POST', null);
};

export const getCustomRoutingFields = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/custom-routing-fields/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateCustomRoutingFields = async (signal, workspaceUuid, fields) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/custom-routing-fields/`;
    return UseAPI(signal, url, 'PUT', { custom_routing_fields: fields });
};

export const getOnboardingChecklist = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/onboarding-checklist/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateOnboardingChecklist = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/onboarding-checklist/`;
    return UseAPI(signal, url, 'PUT', data);
};

// ==========================================
// WORKSPACE INVITATIONS (App - Token Auth)
// ==========================================

export const listPendingInvitations = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/invitations/`;
    return UseAPI(signal, url, 'GET', null);
};

export const cancelInvitation = async (signal, invitationUuid) => {
    const url = `${API_BASE}api/invitations/${invitationUuid}/cancel/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const resendInvitation = async (signal, invitationUuid) => {
    const url = `${API_BASE}api/invitations/${invitationUuid}/resend/`;
    return UseAPI(signal, url, 'POST', null);
};

// ==========================================
// PUBLIC INVITATION ENDPOINTS (No Auth)
// ==========================================

export const getInvitationDetails = async (signal, token) => {
    const url = `${API_BASE}api/invitations/detail/${token}/`;
    return UseAPI(signal, url, 'GET', null, false); // false = no auth required
};

export const acceptInvitation = async (signal, token) => {
    const url = `${API_BASE}api/invitations/accept/${token}/`;
    return UseAPI(signal, url, 'POST', null); // use default auth
};

export const rejectInvitation = async (signal, token) => {
    const url = `${API_BASE}api/invitations/reject/${token}/`;
    return UseAPI(signal, url, 'POST', null, false); // false = no auth required
};

// ==========================================
// WORKSPACE ANALYTICS (App - Token Auth)
// ==========================================

export const getWorkspaceAnalytics = async (signal, workspaceUuid, linkUuid = null, period = '30d') => {
    const params = new URLSearchParams();
    if (linkUuid) params.append('link', linkUuid);
    if (period) params.append('period', period);

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/analytics/dashboard/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getWorkspaceTrends = async (signal, workspaceUuid, period = '30d', linkUuid = null) => {
    const params = `?period=${period}${linkUuid ? `&link=${linkUuid}` : ''}`;
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/analytics/trends/${params}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getLinkPerformance = async (signal, workspaceUuid, limit = 10, period = '30d') => {
    const params = new URLSearchParams({ limit, period });
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/analytics/links/?${params.toString()}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAnalyticsFunnel = async (signal, workspaceUuid, linkUuid = null, period = '30d') => {
    const params = new URLSearchParams();
    if (linkUuid) params.append('link', linkUuid);
    if (period) params.append('period', period);

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/analytics/funnel/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAnalyticsDetailed = async (signal, workspaceUuid, linkUuid = null, period = '30d') => {
    const params = new URLSearchParams();
    if (linkUuid) params.append('link', linkUuid);
    if (period) params.append('period', period);

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/analytics/detailed/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAIInsights = async (signal, workspaceUuid, linkUuid = null, period = '30d', refresh = false) => {
    const params = new URLSearchParams();
    if (linkUuid) params.append('link', linkUuid);
    if (period) params.append('period', period);
    if (refresh) params.append('refresh', 'true');

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/analytics/ai-insights/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// WEBHOOKS (App - Token Auth)
// ==========================================

export const listWebhooks = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/webhooks/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createWebhook = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/webhooks/`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateWebhook = async (signal, workspaceUuid, webhookUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/webhooks/${webhookUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteWebhook = async (signal, workspaceUuid, webhookUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/webhooks/${webhookUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const testWebhook = async (signal, workspaceUuid, webhookUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/webhooks/${webhookUuid}/test/`;
    return UseAPI(signal, url, 'POST', null);
};

// ==========================================
// FEEDBACK LINKS (App - Token Auth)
// ==========================================

export const updateLinkVisualStyle = async (signal, linkUuid, data) => {
    const url = `${API_BASE}api/links/${linkUuid}/visual-style/`;
    return UseAPI(signal, url, 'PATCH', data);
};

// ==========================================
// LINK INTEGRATION ASSIGNMENTS
// ==========================================

// Get integrations assigned to a specific link
export const getLinkIntegrations = async (signal, linkUuid) => {
    const url = `${API_BASE}api/links/${linkUuid}/integrations/`;
    return UseAPI(signal, url, 'GET', null);
};

// Assign integrations to a specific link
export const assignIntegrationsToLink = async (signal, linkUuid, integrationData) => {
    // Guard: if link was not created (undefined UUID), do nothing
    if (!linkUuid) {
        console.error('assignIntegrationsToLink called with undefined linkUuid — skipping');
        return;
    }

    const baseUrl = `${API_BASE}api/links/${linkUuid}/integrations/`;

    // Normalize input to array of string UUIDs
    const selectedUuids = integrationData.map(item => {
        if (typeof item === 'string') return item;
        return item.uuid;
    }).filter(Boolean);

    try {
        await UseAPI(signal, baseUrl, 'POST', { integration_uuids: selectedUuids });
    } catch (e) {
        console.warn('Failed to assign integrations:', e);
        throw e;
    }
};


export const getWebhookDeliveries = async (signal, workspaceUuid, webhookUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/webhooks/${webhookUuid}/deliveries/`;
    return UseAPI(signal, url, 'GET', null);
};

export const retryWebhookDelivery = async (signal, workspaceUuid, webhookUuid, deliveryUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/webhooks/${webhookUuid}/deliveries/${deliveryUuid}/retry/`;
    return UseAPI(signal, url, 'POST', null);
};

export const getWebhookEventTypes = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/webhooks/event-types/`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// INTEGRATION DESTINATIONS (App - Token Auth)
// ==========================================

export const listIntegrations = async (signal, params = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) {
        queryParams.workspace = workspace;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/integrations/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getIntegration = async (signal, integrationUuid) => {
    const url = `${API_BASE}api/integrations/${integrationUuid}/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createIntegration = async (signal, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const bodyData = { ...data };
    if (workspace) {
        bodyData.workspace = workspace;
    }
    const url = `${API_BASE}api/integrations/`;
    return UseAPI(signal, url, 'POST', bodyData);
};

export const updateIntegration = async (signal, integrationUuid, data) => {
    const url = `${API_BASE}api/integrations/${integrationUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteIntegration = async (signal, integrationUuid) => {
    const url = `${API_BASE}api/integrations/${integrationUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const duplicateIntegration = async (signal, integrationUuid) => {
    const url = `${API_BASE}api/integrations/${integrationUuid}/duplicate-auth/`;
    return UseAPI(signal, url, 'POST', null);
};

// Get webhook event payload schemas
export const getWebhookEventSchemas = async (signal, eventType = null, realistic = true) => {
    const params = new URLSearchParams();
    if (eventType) {
        params.append('event', eventType);
    }
    params.append('realistic', realistic.toString());

    const url = `${API_BASE}api/integrations/event-schemas/?${params.toString()}`;
    return UseAPI(signal, url, 'GET', null);
};


export const testIntegration = async (signal, integrationUuid) => {
    const url = `${API_BASE}api/integrations/${integrationUuid}/test/`;
    return UseAPI(signal, url, 'POST', null);
};

export const getIntegrationHealth = async (signal, integrationUuid) => {
    const url = `${API_BASE}api/integrations/${integrationUuid}/health/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getIntegrationLogs = async (signal, integrationUuid, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE}api/integrations/${integrationUuid}/logs/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

// Calendly
export const installCalendly = async (signal) => {
    const url = `${API_BASE}api/integrations/calendly/install/`;
    return UseAPI(signal, url, 'GET', null);
};

export const disconnectCalendly = async (signal, destUuid) => {
    const url = `${API_BASE}api/integrations/calendly/${destUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

// Stripe
export const installStripe = async (signal) => {
    const url = `${API_BASE}api/integrations/stripe/install/`;
    return UseAPI(signal, url, 'GET', null);
};

export const disconnectStripe = async (signal, destUuid) => {
    const url = `${API_BASE}api/integrations/stripe/${destUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const getCalendlyEventTypes = async (signal, destUuid) => {
    const url = `${API_BASE}api/integrations/calendly/${destUuid}/event-types/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getGlobalDefaultIntegration = async (signal) => {
    const url = `${API_BASE}api/integrations/global-default/`;
    return UseAPI(signal, url, 'GET', null);
};

export const listLinkIntegrations = async (signal, linkUuid) => {
    const url = `${API_BASE}api/links/${linkUuid}/integrations/`;
    return UseAPI(signal, url, 'GET', null);
};

export const assignLinkIntegration = async (signal, linkUuid, data) => {
    const url = `${API_BASE}api/links/${linkUuid}/integrations/`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateLinkIntegration = async (signal, linkUuid, assignmentUuid, data) => {
    const url = `${API_BASE}api/links/${linkUuid}/integrations/${assignmentUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const removeLinkIntegration = async (signal, linkUuid, assignmentUuid) => {
    const url = `${API_BASE}api/links/${linkUuid}/integrations/${assignmentUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

// ==========================================
// API TOKENS (App - Token Auth)
// ==========================================

export const listAPITokens = async (signal) => {
    const url = `${API_BASE}api/v1/tokens/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createAPIToken = async (signal, data) => {
    const url = `${API_BASE}api/v1/tokens/`;
    return UseAPI(signal, url, 'POST', data);
};

export const deleteAPIToken = async (signal, tokenUuid) => {
    const url = `${API_BASE}api/v1/tokens/${tokenUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const getAPITokenStats = async (signal) => {
    const url = `${API_BASE}api/v1/tokens/stats/`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// MCP TOKENS (Agent Connector)
// ==========================================

export const listMCPTokens = async (signal) => {
    const url = `${API_BASE}api/mcp/tokens/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createMCPToken = async (signal, data) => {
    const url = `${API_BASE}api/mcp/tokens/`;
    return UseAPI(signal, url, 'POST', data);
};

export const deleteMCPToken = async (signal, tokenUuid) => {
    const url = `${API_BASE}api/mcp/tokens/${tokenUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const getMCPTokenStats = async (signal) => {
    const url = `${API_BASE}api/mcp/tokens/stats/`;
    return UseAPI(signal, url, 'GET', null);
};

export const listMCPAuditLogs = async (signal, limit = 50) => {
    const url = `${API_BASE}api/mcp/audit-logs/?limit=${encodeURIComponent(limit)}`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// ONBOARDING (App - Token Auth)
// ==========================================

export const getOnboardingStatus = async (signal) => {
    const url = `${API_BASE}api/onboarding/status/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getOnboardingTemplateOptions = async (signal) => {
    const url = `${API_BASE}api/onboarding/template-options/`;
    return UseAPI(signal, url, 'GET', null);
};

// First-run /start flow
export const getFirstRunState = async (signal) => {
    const url = `${API_BASE}api/onboarding/start/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getFirstRunProgress = async (signal) => {
    const url = `${API_BASE}api/onboarding/start-progress/`;
    return UseAPI(signal, url, 'GET', null);
};

export const markFirstRunFlag = async (signal, flag, completed = true) => {
    const url = `${API_BASE}api/onboarding/start-progress/`;
    return UseAPI(signal, url, 'POST', { flag, completed });
};

export const createOnboardingWorkflow = async (signal, use_case) => {
    const url = `${API_BASE}api/onboarding/create-workflow/`;
    return UseAPI(signal, url, 'POST', { use_case });
};

export const deleteOnboardingSampleData = async (signal) => {
    const url = `${API_BASE}api/onboarding/sample-data/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const getOnboardingEmptyState = async (signal, area) => {
    const url = `${API_BASE}api/onboarding/empty-state/${area}/`;
    return UseAPI(signal, url, 'GET', null);
};

export const dismissOnboardingNudge = async (signal, nudgeKey) => {
    const url = `${API_BASE}api/onboarding/dismiss-nudge/`;
    return UseAPI(signal, url, 'POST', { key: nudgeKey });
};

export const updateOnboardingProfile = async (signal, data) => {
    const url = `${API_BASE}api/onboarding/update-profile/`;
    return UseAPI(signal, url, 'POST', data);
};

export const selectOnboardingUseCase = async (signal, use_case) => {
    const url = `${API_BASE}api/onboarding/select-use-case/`;
    return UseAPI(signal, url, 'POST', { use_case });
};

export const setupOnboardingWorkspace = async (signal, data) => {
    const url = `${API_BASE}api/onboarding/setup-workspace/`;
    return UseAPI(signal, url, 'POST', data);
};

export const checkWorkspaceAvailability = async (signal, name) => {
    const url = `${API_BASE}api/onboarding/check-workspace/?name=${encodeURIComponent(name)}`;
    return UseAPI(signal, url, 'GET', null);
};

export const checkSubdomainAvailability = async (signal, slug) => {
    const url = `${API_BASE}api/onboarding/check-subdomain/?slug=${encodeURIComponent(slug)}`;
    return UseAPI(signal, url, 'GET', null);
};

export const completeOnboarding = async (signal) => {
    const url = `${API_BASE}api/onboarding/complete/`;
    return UseAPI(signal, url, 'POST', null);
};

export const skipOnboarding = async (signal) => {
    const url = `${API_BASE}api/onboarding/skip/`;
    return UseAPI(signal, url, 'POST', null);
};

// ==========================================
// ACHIEVEMENTS (App - Token Auth)
// ==========================================

export const getAchievementProgress = async (signal) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/achievements/progress/?workspace_uuid=${workspace}`;
    return UseAPI(signal, url, 'GET', null);
};

export const claimAchievementTier = async (signal, tier) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/achievements/claim/${tier}/?workspace_uuid=${workspace}`;
    return UseAPI(signal, url, 'POST', null);
};

export const checkAchievementMilestone = async (signal, milestone) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/achievements/check/?workspace_uuid=${workspace}`;
    return UseAPI(signal, url, 'POST', { milestone });
};

// ==========================================
// REFERRALS (App - Token Auth)
// ==========================================

export const getReferralProfile = async (signal) => {
    const url = `${API_BASE}api/referrals/me/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createReferralLink = async (signal, data) => {
    const url = `${API_BASE}api/referrals/links/`;
    return UseAPI(signal, url, 'POST', data);
};

export const listReferralLinks = async (signal) => {
    const url = `${API_BASE}api/referrals/links/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateReferralLink = async (signal, linkUuid, data) => {
    const url = `${API_BASE}api/referrals/links/${linkUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteReferralLink = async (signal, linkUuid) => {
    const url = `${API_BASE}api/referrals/links/${linkUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const getReferralHistory = async (signal) => {
    const url = `${API_BASE}api/referrals/history/`;
    return UseAPI(signal, url, 'GET', null);
};

export const listReferralPayoutMethods = async (signal) => {
    const url = `${API_BASE}api/referrals/payout-methods/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createReferralPayoutMethod = async (signal, data) => {
    const url = `${API_BASE}api/referrals/payout-methods/`;
    return UseAPI(signal, url, 'POST', data);
};

export const deleteReferralPayoutMethod = async (signal, methodUuid) => {
    const url = `${API_BASE}api/referrals/payout-method/${methodUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const listReferralPayoutRequests = async (signal) => {
    const url = `${API_BASE}api/referrals/payouts/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createReferralPayoutRequest = async (signal, data) => {
    const url = `${API_BASE}api/referrals/payouts/`;
    return UseAPI(signal, url, 'POST', data);
};

// ==========================================
// ADMIN REFERRAL MANAGEMENT (Staff Only)
// ==========================================

export const getAdminReferralProfiles = async (signal) => {
    const url = `${API_BASE}api/admin/referrals/profiles/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAdminReferralConversions = async (signal) => {
    const url = `${API_BASE}api/admin/referrals/conversions/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAdminReferralRewards = async (signal) => {
    const url = `${API_BASE}api/admin/referrals/rewards/`;
    return UseAPI(signal, url, 'GET', null);
};

export const approveAdminReferralReward = async (signal, uuid, data) => {
    const url = `${API_BASE}api/admin/referrals/rewards/${uuid}/approve/`;
    return UseAPI(signal, url, 'POST', data);
};

export const voidAdminReferralReward = async (signal, uuid, data) => {
    const url = `${API_BASE}api/admin/referrals/rewards/${uuid}/void/`;
    return UseAPI(signal, url, 'POST', data);
};

// ==========================================
// TENANT MANAGEMENT (App - Token Auth)
// ==========================================

export const getTenantConfig = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-config/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateTenantConfig = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-config/`;
    return UseAPI(signal, url, 'PUT', data);
};

export const getTenantBranding = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-branding/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateTenantBranding = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-branding/`;
    return UseAPI(signal, url, 'PUT', data);
};

export const applyTenantBranding = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-branding/apply/`;
    return UseAPI(signal, url, 'POST', data);
};

export const getTenantCollectorTheme = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-collector-theme/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateTenantCollectorTheme = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-collector-theme/`;
    return UseAPI(signal, url, 'PUT', data);
};

export const updateTenantVisualStyle = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-visual-style/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const listTenantCustomDomains = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-custom-domain/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createTenantCustomDomain = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-custom-domain/`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateTenantCustomDomain = async (signal, workspaceUuid, domainId, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-custom-domain/${domainId}/`;
    return UseAPI(signal, url, 'PUT', data);
};

export const deleteTenantCustomDomain = async (signal, workspaceUuid, domainId) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-custom-domain/${domainId}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const verifyTenantCustomDomain = async (signal, workspaceUuid, domainId) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-custom-domain/${domainId}/verify/`;
    return UseAPI(signal, url, 'POST', {});
};

export const checkTenantCustomDomainSSL = async (signal, workspaceUuid, domainId) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-custom-domain/${domainId}/check-ssl/`;
    return UseAPI(signal, url, 'POST', {});
};

export const checkSlugAvailability = async (signal, slug) => {
    const url = `${API_BASE}api/tenants/slug-check/?slug=${encodeURIComponent(slug)}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getTenantAssetUploadUrl = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/tenant-assets/upload/`;
    return UseAPI(signal, url, 'POST', data);
};

// Public tenant branding (no auth required)
export const getTenantPublicBranding = async (signal, linkSlug) => {
    const url = `${API_BASE}api/public/link/${linkSlug}/branding/`;
    return UseAPI(signal, url, 'GET', null, false, getTenantHostHeader()); // false = no auth
};

export const resolvePublicBranding = async (signal, domain = window.location.hostname) => {
    const url = `${API_BASE}api/public/branding/resolve/?domain=${encodeURIComponent(domain)}`;
    return UseAPI(signal, url, 'GET', null, false, getTenantHostHeader());
};

// ==========================================
// EMAIL MARKETING AUTOMATIONS (App - Token Auth)
// ==========================================

export const listAutomations = async (signal) => {
    const url = `${API_BASE}api/admin/email-marketing/automations/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAutomation = async (signal, automationUuid) => {
    const url = `${API_BASE}api/admin/email-marketing/automations/${automationUuid}/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createAutomation = async (signal, data) => {
    const url = `${API_BASE}api/admin/email-marketing/automations/`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateAutomation = async (signal, automationUuid, data) => {
    const url = `${API_BASE}api/admin/email-marketing/automations/${automationUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteAutomation = async (signal, automationUuid) => {
    const url = `${API_BASE}api/admin/email-marketing/automations/${automationUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

// ==========================================
// WORKSPACE EMAIL TEMPLATES (For Workflow Builder)
// ==========================================

export const listWorkspaceEmailTemplates = async (signal) => {
    const url = `${API_BASE}api/workspace-email-templates/`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// PDF TEMPLATES (For Workflow Builder)
// ==========================================

export const listPDFTemplates = async (signal) => {
    const url = `${API_BASE}api/pdf/`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// EMAIL DOMAIN VERIFICATION (App - Token Auth)
// ==========================================

export const listEmailDomains = async (signal) => {
    const url = `${API_BASE}api/email-domains/`;
    return UseAPI(signal, url, 'GET', null);
};

export const initiateEmailDomainVerification = async (signal, domain) => {
    const url = `${API_BASE}api/email-domains/`;
    return UseAPI(signal, url, 'POST', { domain });
};

export const checkEmailDomainStatus = async (signal, domainUuid) => {
    const url = `${API_BASE}api/email-domains/${domainUuid}/verify/`;
    return UseAPI(signal, url, 'POST', null);
};

export const deleteEmailDomain = async (signal, domainUuid) => {
    const url = `${API_BASE}api/email-domains/${domainUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

// ── Email send tracking ──
export const getSentContactsForForm = async (signal, formLinkUuid) => {
    const url = `${API_BASE}api/workspace-email-templates/sent-contacts/?form_link_uuid=${formLinkUuid}`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// WORKSPACE SETTINGS - Email & Twilio (App - Token Auth)
// ==========================================

export const getWorkspaceEmailSettings = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/settings/email/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateWorkspaceEmailSettings = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/settings/email/`;
    return UseAPI(signal, url, 'PUT', data);
};

export const getWorkspaceTwilioSettings = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/settings/twilio/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateWorkspaceTwilioSettings = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/settings/twilio/`;
    return UseAPI(signal, url, 'PUT', data);
};

export const getWorkspaceLocalizationSettings = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/settings/localization/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateWorkspaceLocalizationSettings = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/settings/localization/`;
    return UseAPI(signal, url, 'PUT', data);
};

export const getCaseManagementSettings = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/settings/case-management/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateCaseManagementSettings = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/settings/case-management/`;
    return UseAPI(signal, url, 'PUT', data);
};

// ==========================================
// NOTIFICATION SETTINGS (App - Token Auth)
// ==========================================

export const getNotificationSettings = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/notification-settings/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateNotificationSettings = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/notification-settings/`;
    return UseAPI(signal, url, 'PUT', data);
};

export const testNotificationChannel = async (signal, workspaceUuid, channel) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/notification-settings/test/`;
    return UseAPI(signal, url, 'POST', { channel });
};

export const getSamplePayload = async (signal, workspaceUuid, channel) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/notification-settings/sample/?channel=${channel}`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// TTS (Text-to-Speech) APIs (App - Token Auth)
// ==========================================

export const getTTSVoices = async (signal) => {
    const url = `${API_BASE}api/tts/voices/`;
    return UseAPI(signal, url, 'GET', null);
};

export const previewTTSVoice = async (signal, data) => {
    const url = `${API_BASE}api/tts/preview/`;
    return UseAPI(signal, url, 'POST', data);
};

// ==========================================
// DOCUMENTS (App - Token Auth)
// ==========================================

export const assignCaseToMe = async (signal, caseUuid) => {
    const url = `${API_BASE}api/inbox/${caseUuid}/assign_to_me/`;
    return UseAPI(signal, url, 'POST', null);
};

export const exportInboxCsv = async (params = {}) => {
    const token = localStorage.getItem('user-token');
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    
    const url = `${API_BASE}api/inbox/export-csv/${queryString ? `?${queryString}` : ''}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inbox_cases_export.csv';
    
    return fetch(url, { headers: { 'Authorization': `Token ${token}` } })
        .then(r => r.blob())
        .then(blob => {
            const blobUrl = window.URL.createObjectURL(blob);
            a.href = blobUrl;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
            return true;
        })
        .catch(err => {
            console.error('Download failed', err);
            return false;
        });
};

export const listDocuments = async (signal, linkUuid) => {
    const url = `${API_BASE}api/links/${linkUuid}/documents/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createDocument = async (signal, linkUuid, data) => {
    const url = `${API_BASE}api/links/${linkUuid}/documents/`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateDocument = async (signal, linkUuid, documentUuid, data) => {
    const url = `${API_BASE}api/links/${linkUuid}/documents/${documentUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteDocument = async (signal, linkUuid, documentUuid) => {
    const url = `${API_BASE}api/links/${linkUuid}/documents/${documentUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const reorderDocuments = async (signal, linkUuid, orderedUuids) => {
    const url = `${API_BASE}api/links/${linkUuid}/documents/reorder/`;
    return UseAPI(signal, url, 'POST', { ordered_uuids: orderedUuids });
};

export const getDocumentUploadUrl = async (signal, linkUuid, fileName, contentType) => {
    const url = `${API_BASE}api/links/${linkUuid}/documents/upload-url/`;
    return UseAPI(signal, url, 'POST', { file_name: fileName, content_type: contentType });
};

export const listWorkspaceDocuments = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/documents/`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// PUBLIC DOCUMENTS (No Auth)
// ==========================================

export const getPublicDocuments = async (signal, linkSlug) => {
    const url = `${API_BASE}api/public/links/${linkSlug}/documents/`;
    return UseAPI(signal, url, 'GET', null, false, getTenantHostHeader());
};

export const getDocumentDownloadUrl = async (signal, slug, docUuid) => {
    const url = `${API_BASE}api/public/links/${slug}/documents/${docUuid}/download/`;
    return UseAPI(signal, url, 'GET', null, false, getTenantHostHeader());
};

export const trackDocumentDownload = async (signal, linkSlug, documentUuid) => {
    const url = `${API_BASE}api/public/links/${linkSlug}/documents/${documentUuid}/track-download/`;
    return UseAPI(signal, url, 'POST', null, false, getTenantHostHeader());
};

// ==========================================
// CONTACTS / CRM (App - Token Auth)
// ==========================================

export const listContacts = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/contacts/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const exportContactsCsv = async (params = {}) => {
    const token = localStorage.getItem('user-token');
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    
    const url = `${API_BASE}api/contacts/export-csv/${queryString ? `?${queryString}` : ''}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_export.csv';
    
    return fetch(url, { headers: { 'Authorization': `Token ${token}` } })
        .then(r => r.blob())
        .then(blob => {
            const blobUrl = window.URL.createObjectURL(blob);
            a.href = blobUrl;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
            return true;
        })
        .catch(err => {
            console.error('Download failed', err);
            return false;
        });
};

export const getContact = async (signal, contactUuid) => {
    const url = `${API_BASE}api/contacts/${contactUuid}/`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateContact = async (signal, contactUuid, data) => {
    const workspace = data?.workspace || localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const payload = { ...data };
    delete payload.workspace;
    const url = `${API_BASE}api/contacts/${contactUuid}/${queryString}`;
    return UseAPI(signal, url, 'PATCH', payload);
};

export const resolveContactEmail = async (signal, data) => {
    const workspace = data?.workspace || localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/contacts/resolve-email/${queryString}`;
    const payload = { ...data };
    delete payload.workspace;
    return UseAPI(signal, url, 'POST', payload);
};

export const deleteContact = async (signal, contactUuid) => {
    const url = `${API_BASE}api/contacts/${contactUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const mergeContacts = async (signal, primaryUuid, secondaryUuid) => {
    const url = `${API_BASE}api/contacts/${primaryUuid}/merge/`;
    return UseAPI(signal, url, 'POST', { secondary_uuid: secondaryUuid });
};

export const createContact = async (signal, data) => {
    const workspace = data?.workspace || localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const payload = { ...data };
    delete payload.workspace;
    const url = `${API_BASE}api/contacts/${queryString}`;
    return UseAPI(signal, url, 'POST', payload);
};

export const uploadContactsCsv = async (signal, file) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/contacts/upload-csv/?workspace=${workspace}`;
    const formData = new FormData();
    formData.append('file', file);
    // Use fetch directly for FormData
    const token = localStorage.getItem('user-token');
    const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}` },
        body: formData,
        signal,
    });
    return res.json();
};

export const downloadContactTemplate = () => {
    const token = localStorage.getItem('user-token');
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/contacts/download-template/?workspace=${workspace}`;
    // Trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template.csv';
    // Use fetch to handle auth
    fetch(url, { headers: { 'Authorization': `Token ${token}` } })
        .then(r => r.blob())
        .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            a.href = blobUrl;
            a.click();
            URL.revokeObjectURL(blobUrl);
        });
};

export const sendFormEmail = async (signal, templateUuid, data) => {
    const url = `${API_BASE}api/workspace-email-templates/${templateUuid}/send-form-email/`;
    const workspace = localStorage.getItem('current_workspace_uuid');
    const payload = workspace ? { ...data, workspace } : data;
    return UseAPI(signal, url, 'POST', payload);
};

export const listFormEmailLogs = async (signal, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE}api/workspace-email-templates/email-logs/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getOutreachRecipients = async (signal, formLinkUuid, params = {}) => {
    const allParams = { ...params };
    const workspace = localStorage.getItem('current_workspace_uuid');
    if (workspace) allParams.workspace = workspace;
    if (formLinkUuid) allParams.form_link_uuid = formLinkUuid;
    const queryParams = new URLSearchParams(allParams);
    const url = `${API_BASE}api/workspace-email-templates/outreach-recipients/?${queryParams}`;
    return UseAPI(signal, url, 'GET', null);
};

export const listEmailCampaigns = async (signal, params = {}) => {
    const allParams = { ...params };
    const workspace = localStorage.getItem('current_workspace_uuid');
    if (workspace) allParams.workspace = workspace;
    const queryParams = new URLSearchParams(allParams);
    const url = `${API_BASE}api/workspace-email-templates/campaigns/?${queryParams}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getWorkspaceEmailStats = async (signal) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = new URLSearchParams(workspace ? { workspace } : {}).toString();
    const url = `${API_BASE}api/workspace-email-templates/workspace-email-stats/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

// Default export with all methods
const SayifyAPI = {
    listLinks,
    getLink,
    createLink,
    updateLink,
    updateLinkBranding,
    updateLinkLayout,
    deleteLink,
    duplicateLink,
    checkLinkSlugAvailability,
    listResponses,
    getResponse,
    getPublicLink,
    startResponseSession,
    submitAnswer,
    getAnswerEvaluationStatus,
    reportConfigError,
    submitSession,
    // Workspace methods
    listWorkspaces,
    getWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    switchWorkspace,
    listWorkspaceMembers,
    inviteMember,
    updateMemberRole,
    removeMember,
    leaveWorkspace,
    listPendingInvitations,
    cancelInvitation,
    resendInvitation,
    getInvitationDetails,
    acceptInvitation,
    rejectInvitation,
    getCaseManagementSettings,
    updateCaseManagementSettings,
    getWorkspaceLocalizationSettings,
    updateWorkspaceLocalizationSettings,
    // Analytics methods
    getWorkspaceAnalytics,
    getWorkspaceTrends,
    getLinkPerformance,
    getAnalyticsFunnel,
    getAnalyticsDetailed,
    getAIInsights,
    // Webhook methods
    listWebhooks,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    testWebhook,
    getWebhookDeliveries,
    retryWebhookDelivery,
    getWebhookEventTypes,
    // API Token methods
    listAPITokens,
    createAPIToken,
    deleteAPIToken,
    getAPITokenStats,
    // Export methods
    exportResponses,
    // Referral methods
    getReferralProfile,
    createReferralLink,
    listReferralLinks,
    updateReferralLink,
    deleteReferralLink,
    getReferralHistory,
    listReferralPayoutMethods,
    createReferralPayoutMethod,
    deleteReferralPayoutMethod,
    listReferralPayoutRequests,
    createReferralPayoutRequest,
    // Admin Referral methods
    getAdminReferralProfiles,
    getAdminReferralConversions,
    getAdminReferralRewards,
    approveAdminReferralReward,
    voidAdminReferralReward,
    // Tenant methods
    getTenantConfig,
    updateTenantConfig,
    getTenantBranding,
    updateTenantBranding,
    getTenantCollectorTheme,
    updateTenantCollectorTheme,
    listTenantCustomDomains,
    createTenantCustomDomain,
    updateTenantCustomDomain,
    deleteTenantCustomDomain,
    verifyTenantCustomDomain,
    checkTenantCustomDomainSSL,
    checkSlugAvailability,
    getTenantAssetUploadUrl,
    getTenantPublicBranding,
    resolvePublicBranding,
    // Integration Destination methods
    listIntegrations,
    getIntegrationStats,
    getIntegration,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    testIntegration,
    getIntegrationHealth,
    getIntegrationLogs,
    getGlobalDefaultIntegration,
    listLinkIntegrations,
    assignLinkIntegration,
    updateLinkIntegration,
    removeLinkIntegration,
    // Onboarding
    getOnboardingStatus,
    getFirstRunState,
    getFirstRunProgress,
    markFirstRunFlag,
    getOnboardingTemplateOptions,
    createOnboardingWorkflow,
    deleteOnboardingSampleData,
    getOnboardingEmptyState,
    dismissOnboardingNudge,
    updateOnboardingProfile,
    selectOnboardingUseCase,
    setupOnboardingWorkspace,
    checkWorkspaceAvailability,
    checkSubdomainAvailability,
    completeOnboarding,
    skipOnboarding,
    // Achievement methods
    getAchievementProgress,
    claimAchievementTier,
    checkAchievementMilestone,
    // TTS methods
    getTTSVoices,
    previewTTSVoice,
    // Document methods
    listDocuments,
    listWorkspaceDocuments,
    createDocument,
    updateDocument,
    deleteDocument,
    reorderDocuments,
    getDocumentUploadUrl,
    getPublicDocuments,
    trackDocumentDownload,
    // Contact methods
    listContacts,
    getContact,
    createContact,
    updateContact,
    deleteContact,
    mergeContacts,
    uploadContactsCsv,
    downloadContactTemplate,
    exportContactsCsv,
    sendFormEmail,
    listFormEmailLogs,
    listInboxCases,
    getInboxCase,
    updateInboxCase,
    deleteInboxCase,
    assignCaseToMe,
    exportInboxCsv,
    listSavedViews,
    createSavedView,
    updateSavedView,
    deleteSavedView,
};

export default SayifyAPI;


// ==========================================
// TEMPLATES (App - Token Auth)
// ==========================================

export const listTemplates = async (signal, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE}api/templates/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getTemplate = async (signal, slug) => {
    const url = `${API_BASE}api/templates/${slug}/`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// PAGE TEMPLATES - Welcome/Thank You (App Auth)
// ==========================================

export const listPageTemplates = async (signal, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE}api/page-templates/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getPageTemplate = async (signal, slug) => {
    const url = `${API_BASE}api/page-templates/${slug}/`;
    return UseAPI(signal, url, 'GET', null);
};


// ==========================================
// INBOX REPLIES (App - Token Auth)
// ==========================================

export const listInboxReplies = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/inbox/replies/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const listInboxReplyThreads = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/inbox/replies/threads/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const markRepliesRead = async (signal, uuids) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/replies/${queryString}`;
    return UseAPI(signal, url, 'PATCH', { uuids });
};

// ==========================================
// INBOX CASES (App - Token Auth)
// ==========================================

export const listInboxCases = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/inbox/cases/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getInboxCase = async (signal, caseUuid, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateInboxCase = async (signal, caseUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/${queryString}`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const analyzeInboxCase = async (signal, caseUuid, data = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/analyze/${queryString}`;
    return UseAPI(signal, url, 'POST', data);
};

export const correctExtractedField = async (caseUuid, fieldKey, correctedValue) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/correct-field/${queryString}`;
    return UseAPI(null, url, 'POST', { field_key: fieldKey, corrected_value: correctedValue });
};

export const bulkActionInboxCases = async (signal, action, uuids, extraData = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/inbox/cases/bulk-action/`;
    return UseAPI(signal, url, 'POST', { action, uuids, workspace, ...extraData });
};

// ==========================================
// CASE EVENTS / TIMELINE (App - Token Auth)
// ==========================================

export const getCaseEvents = async (signal, caseUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/events/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getCaseNotes = async (signal, caseUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/notes/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const createCaseNote = async (signal, caseUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/notes/${queryString}`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateCaseNote = async (signal, caseUuid, noteUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/notes/${noteUuid}/${queryString}`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteCaseNote = async (signal, caseUuid, noteUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/notes/${noteUuid}/${queryString}`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const generateCaseNoteAIActions = async (signal, caseUuid, noteUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/notes/${noteUuid}/ai-actions/${queryString}`;
    return UseAPI(signal, url, 'POST', {});
};

export const applyCaseNoteAction = async (signal, caseUuid, noteUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/notes/${noteUuid}/apply-action/${queryString}`;
    return UseAPI(signal, url, 'POST', data);
};

export const getCaseDecisions = async (signal, caseUuid, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/decisions/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getExtractedFieldValues = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/inbox/extracted-field-values/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

// CASE ACTIONS (App - Token Auth)
// ==========================================

export const listCaseActions = async (signal, caseUuid, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/actions/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const createCaseAction = async (signal, caseUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/actions/${queryString}`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateCaseAction = async (signal, actionUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/actions/${actionUuid}/${queryString}`;
    return UseAPI(signal, url, 'PATCH', data);
};
// ==========================================
// GLOBAL ACTIONS (App - Token Auth)
// ==========================================

export const listActions = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/actions/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};


// ==========================================
// PLAYBOOK CONFIG (App - Token Auth)
// ==========================================

export const getPlaybook = async (signal, useCase) => {
    const url = `${API_BASE}api/playbook/${useCase}/`;
    return UseAPI(signal, url, 'GET', null);
};


// ==========================================
// CASE EMAILS / COMPOSE (App - Token Auth)
// ==========================================

export const getCaseEmails = async (signal, caseUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/emails/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const sendCaseEmail = async (signal, caseUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/emails/${queryString}`;
    return UseAPI(signal, url, 'POST', data);
};

export const getInboxKPIs = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/inbox/kpis/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// CASE ALERTS (App - Token Auth)
// ==========================================

export const listCaseAlerts = async (signal, caseUuid, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/alerts/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const createCaseAlert = async (signal, caseUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/alerts/${queryString}`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateCaseAlert = async (signal, alertUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/alerts/${alertUuid}/${queryString}`;
    return UseAPI(signal, url, 'PATCH', data);
};


// ==========================================
// GLOBAL ALERTS (App - Token Auth)
// ==========================================

export const listAlerts = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/alerts/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};


// ==========================================
// SIDEBAR COUNTS (App - Token Auth)
// ==========================================

export const getSidebarCounts = async (signal) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/sidebar/counts/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};


// ==========================================
// DESTINATIONS (App - Token Auth)
// ==========================================

export const listDestinations = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getDestination = async (signal, uuid, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${uuid}/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const createDestination = async (signal, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/destinations/`;
    return UseAPI(signal, url, 'POST', { ...data, workspace });
};

export const updateDestination = async (signal, uuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${uuid}/${queryString}`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteDestination = async (signal, uuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${uuid}/${queryString}`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const listDestinationItems = async (signal, uuid, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/destinations/${uuid}/items/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const updateDestinationItem = async (signal, destUuid, itemUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${destUuid}/items/${itemUuid}/${queryString}`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const approveDestinationItem = async (signal, destUuid, itemUuid, integrationUuids = [], options = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${destUuid}/items/${itemUuid}/approve/${queryString}`;
    return UseAPI(signal, url, 'POST', { integration_uuids: integrationUuids, ...options });
};

export const rejectDestinationItem = async (signal, destUuid, itemUuid, options = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${destUuid}/items/${itemUuid}/reject/${queryString}`;
    return UseAPI(signal, url, 'POST', options);
};

export const requestDestinationItemInfo = async (signal, destUuid, itemUuid, message, options = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${destUuid}/items/${itemUuid}/needs-info/${queryString}`;
    return UseAPI(signal, url, 'POST', { message, ...options });
};

export const resubmitDestinationItem = async (signal, destUuid, itemUuid, values = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${destUuid}/items/${itemUuid}/resubmit/${queryString}`;
    return UseAPI(signal, url, 'POST', { values });
};

export const bulkDestinationAction = async (signal, destUuid, action, itemUuids, integrationUuids = [], options = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${destUuid}/items/bulk-action/${queryString}`;
    return UseAPI(signal, url, 'POST', { action, item_uuids: itemUuids, integration_uuids: integrationUuids, ...options });
};

export const listDestinationColumns = async (signal, uuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${uuid}/columns/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const addDestinationColumn = async (signal, uuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${uuid}/columns/${queryString}`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateDestinationColumn = async (signal, uuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${uuid}/columns/${queryString}`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteDestinationColumn = async (signal, uuid, columnUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/destinations/${uuid}/columns/${queryString}`;
    return UseAPI(signal, url, 'DELETE', { uuid: columnUuid });
};


// ==========================================
// REPORTS (App - Token Auth)
// ==========================================

export const getReportFormsOverview = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/reports/forms/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getFormReport = async (signal, formUuid, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/reports/form/${formUuid}/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getThemeResponses = async (signal, formUuid, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/reports/form/${formUuid}/theme-responses/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getQuestionReport = async (signal, questionUuid, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/reports/question/${questionUuid}/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const exportReport = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    // Use export_format instead of format to avoid DRF reserved param conflict
    if (queryParams.format) {
        queryParams.export_format = queryParams.format;
        delete queryParams.format;
    }
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/reports/export/?${queryString}`;
    const data = await UseAPI(signal, url, 'GET', null);

    if (data?.httpStatusCode && data.httpStatusCode !== 200) {
        throw new Error(data.message || data.error || 'Export failed');
    }

    // Decode base64 content from JSON response
    const byteChars = atob(data.content);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
        byteArray[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: data.content_type });
    const filename = data.filename || `report.${params.format || 'csv'}`;

    return { blob, filename };
};

export const getReportFilterOptions = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/reports/filter-options/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const generateBulkReport = async (signal, formUuid, question) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/reports/analyze-bulk/${queryString}`;
    return UseAPI(signal, url, 'POST', { form_uuid: formUuid, question });
};

export const getBulkReportStatus = async (signal, reportUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/reports/analyze-bulk/${reportUuid}/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getBulkReportHistory = async (signal, formUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/reports/analyze-bulk/history/${formUuid}/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// ACTION PLAN — AI-Generated (App - Token Auth)
// ==========================================

export const getActionPlan = async (signal, formUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/reports/action-plan/${formUuid}/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};

export const toggleActionPlanItem = async (signal, formUuid, itemId, newStatus) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/reports/action-plan/${formUuid}/${queryString}`;
    return UseAPI(signal, url, 'PATCH', { item_id: itemId, status: newStatus });
};

export const refreshActionPlan = async (signal, formUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/reports/action-plan/${formUuid}/${queryString}`;
    return UseAPI(signal, url, 'POST', {});
};

// ==========================================
// TASKS — Kanban Board (App - Token Auth)
// ==========================================

export const getTaskBoard = async (signal, params = {}) => {
    const workspace = params.workspace || localStorage.getItem('current_workspace_uuid');
    const queryParams = { ...params };
    if (workspace) queryParams.workspace = workspace;
    // Remove undefined/null values
    Object.keys(queryParams).forEach(k => { if (!queryParams[k]) delete queryParams[k]; });
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}api/tasks/board/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const createStandaloneTask = async (signal, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/`;
    return UseAPI(signal, url, 'POST', { ...data, workspace });
};

export const createTaskFromReport = async (signal, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/from-report/`;
    return UseAPI(signal, url, 'POST', { ...data, workspace });
};

export const createTaskFromAlert = async (signal, alertUuid, data = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/from-alert/${alertUuid}/?workspace=${workspace}`;
    return UseAPI(signal, url, 'POST', data);
};

export const batchUpdateTaskStatus = async (signal, updates) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/batch-status/`;
    return UseAPI(signal, url, 'PATCH', { updates, workspace });
};

export const updateTask = async (signal, actionUuid, data) => {
    const url = `${API_BASE}api/actions/${actionUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteTask = async (signal, taskUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/${taskUuid}/delete/?workspace=${workspace}`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const getTaskDetail = async (signal, taskUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/${taskUuid}/detail/?workspace=${workspace}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAssignableMembers = async (signal) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/assignable-members/?workspace=${workspace}`;
    return UseAPI(signal, url, 'GET', null);
};

// ==========================================
// SLACK INTEGRATION (App - Token Auth)
// ==========================================

export const getSlackInstallUrl = async (signal) => {
    const url = `${API_BASE}api/integrations/slack/install/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getSlackChannels = async (signal, destUuid) => {
    const url = `${API_BASE}api/integrations/slack/channels/${destUuid}/`;
    return UseAPI(signal, url, 'GET', null);
};

export const setSlackChannel = async (signal, destUuid, data) => {
    const url = `${API_BASE}api/integrations/slack/${destUuid}/channel/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const disconnectSlack = async (signal, destUuid) => {
    const url = `${API_BASE}api/integrations/slack/${destUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

// ==========================================
// GOOGLE SHEETS INTEGRATION (App - Token Auth)
// ==========================================

export const getGoogleSheetsInstallUrl = async (signal) => {
    const url = `${API_BASE}api/integrations/google-sheets/install/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getGoogleSheetsSpreadsheets = async (signal, destUuid) => {
    const url = `${API_BASE}api/integrations/google-sheets/spreadsheets/${destUuid}/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getGoogleSheetsPickerToken = async (signal, destUuid) => {
    const url = `${API_BASE}api/integrations/google-sheets/${destUuid}/picker-token/`;
    return UseAPI(signal, url, 'GET', null);
};

export const configureGoogleSheet = async (signal, destUuid, data) => {
    const url = `${API_BASE}api/integrations/google-sheets/${destUuid}/configure/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const disconnectGoogleSheets = async (signal, destUuid) => {
    const url = `${API_BASE}api/integrations/google-sheets/${destUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const getGoogleSheetsTabs = async (signal, destUuid) => {
    const url = `${API_BASE}api/integrations/google-sheets/${destUuid}/sheets/`;
    return UseAPI(signal, url, 'GET', null);
};


// ==========================================
// BOARD COLUMNS (App - Token Auth)
// ==========================================

export const listBoardColumns = async (signal) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/board-columns/?workspace=${workspace}`;
    return UseAPI(signal, url, 'GET', null);
};

export const createBoardColumn = async (signal, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/board-columns/?workspace=${workspace}`;
    return UseAPI(signal, url, 'POST', data);
};

export const updateBoardColumn = async (signal, columnKey, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/board-columns/${columnKey}/?workspace=${workspace}`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deleteBoardColumn = async (signal, columnKey) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/board-columns/${columnKey}/?workspace=${workspace}`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const reorderBoardColumns = async (signal, order) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/board-columns/reorder/?workspace=${workspace}`;
    return UseAPI(signal, url, 'PATCH', { order });
};


// ==========================================
// TASK COMMENTS (App - Token Auth)
// ==========================================

export const listTaskComments = async (signal, taskUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/${taskUuid}/comments/?workspace=${workspace}`;
    return UseAPI(signal, url, 'GET', null);
};

export const createTaskComment = async (signal, taskUuid, body, attachment = null) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/${taskUuid}/comments/?workspace=${workspace}`;
    const payload = { body };
    if (attachment) {
        payload.attachment_s3_key = attachment.s3_key;
        payload.attachment_url = attachment.s3_url;
        payload.attachment_name = attachment.name;
        payload.attachment_size = attachment.size;
        payload.attachment_type = attachment.type;
    }
    return UseAPI(signal, url, 'POST', payload);
};

export const getTaskCommentUploadUrl = async (signal, taskUuid, fileName, contentType) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/${taskUuid}/comments/upload-url/?workspace=${workspace}`;
    return UseAPI(signal, url, 'POST', { file_name: fileName, content_type: contentType });
};

export const toggleWatchTask = async (signal, taskUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/${taskUuid}/watch/?workspace=${workspace}`;
    return UseAPI(signal, url, 'POST', {});
};


export const updateTaskComment = async (signal, commentUuid, body) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/comments/${commentUuid}/?workspace=${workspace}`;
    return UseAPI(signal, url, 'PATCH', { body });
};

export const deleteTaskComment = async (signal, commentUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/tasks/comments/${commentUuid}/?workspace=${workspace}`;
    return UseAPI(signal, url, 'DELETE', null);
};

// ==========================================
// KNOWLEDGE BASES (App - Token Auth)
// ==========================================

export const listKnowledgeBases = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/knowledge-bases/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createKnowledgeBase = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/knowledge-bases/`;
    return UseAPI(signal, url, 'POST', data);
};

export const deleteKnowledgeBase = async (signal, workspaceUuid, kbUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/knowledge-bases/${kbUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const listKnowledgeDocuments = async (signal, workspaceUuid, kbUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/knowledge-bases/${kbUuid}/documents/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createKnowledgeDocument = async (signal, workspaceUuid, kbUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/knowledge-bases/${kbUuid}/documents/`;
    return UseAPI(signal, url, 'POST', data);
};

export const deleteKnowledgeDocument = async (signal, workspaceUuid, kbUuid, docUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/knowledge-bases/${kbUuid}/documents/${docUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

// ==========================================
// POLICY PACKS (App - Token Auth)
// ==========================================

export const listPolicyPacks = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/`;
    return UseAPI(signal, url, 'GET', null);
};

export const listPolicyPackTemplates = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/templates/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createPolicyPack = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/`;
    return UseAPI(signal, url, 'POST', data);
};

export const createPolicyPackFromTemplate = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/from-template/`;
    return UseAPI(signal, url, 'POST', data);
};

export const updatePolicyPack = async (signal, workspaceUuid, packUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/${packUuid}/`;
    return UseAPI(signal, url, 'PATCH', data);
};

export const deletePolicyPack = async (signal, workspaceUuid, packUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/${packUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const getPolicyPackUploadUrl = async (signal, workspaceUuid, packUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/${packUuid}/upload-url/`;
    return UseAPI(signal, url, 'POST', data);
};

export const listPolicyPackSources = async (signal, workspaceUuid, packUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/${packUuid}/sources/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createPolicyPackSource = async (signal, workspaceUuid, packUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/${packUuid}/sources/`;
    return UseAPI(signal, url, 'POST', data);
};

export const deletePolicyPackSource = async (signal, workspaceUuid, packUuid, sourceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/${packUuid}/sources/${sourceUuid}/`;
    return UseAPI(signal, url, 'DELETE', null);
};

export const reprocessPolicyPackSource = async (signal, workspaceUuid, packUuid, sourceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/policy-packs/${packUuid}/sources/${sourceUuid}/reprocess/`;
    return UseAPI(signal, url, 'POST', null);
};

// ==========================================
// PAYMENTS & COMMERCE (Stripe)
// ==========================================

export const validateStripeCoupon = async (signal, data) => {
    const url = `${API_BASE}api/public/payments/validate-coupon/`;
    return UseAPI(signal, url, 'POST', data);
};

export const listStripeCoupons = async (signal, workspaceUuid) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/stripe-coupons/`;
    return UseAPI(signal, url, 'GET', null);
};

export const createStripeCoupon = async (signal, workspaceUuid, data) => {
    const url = `${API_BASE}api/workspaces/${workspaceUuid}/stripe-coupons/`;
    return UseAPI(signal, url, 'POST', data);
};

// ==========================================
// DASHBOARD PHASE 4 (App - Token Auth)
// ==========================================

export const getDashboardTabs = async (signal) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const url = `${API_BASE}api/dashboard/tabs/${workspace ? `?workspace=${workspace}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getDashboardOverview = async (signal, period = '30d') => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const params = new URLSearchParams();
    if (workspace) params.append('workspace', workspace);
    if (period) params.append('period', period);
    const url = `${API_BASE}api/dashboard/overview/?${params.toString()}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getDashboardPrioritySignals = async (signal, limit = 8) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const params = new URLSearchParams();
    if (workspace) params.append('workspace', workspace);
    if (limit) params.append('limit', String(limit));
    const url = `${API_BASE}api/dashboard/priority-signals/?${params.toString()}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getDashboardLeadQual = async (signal, period = '30d') => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const params = new URLSearchParams();
    if (workspace) params.append('workspace', workspace);
    if (period) params.append('period', period);
    const url = `${API_BASE}api/dashboard/lead-qualification/?${params.toString()}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getDashboardClientIntake = async (signal, period = '30d') => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const params = new URLSearchParams();
    if (workspace) params.append('workspace', workspace);
    if (period) params.append('period', period);
    const url = `${API_BASE}api/dashboard/client-intake/?${params.toString()}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getDashboardSupportTriage = async (signal, period = '30d') => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const params = new URLSearchParams();
    if (workspace) params.append('workspace', workspace);
    if (period) params.append('period', period);
    const url = `${API_BASE}api/dashboard/support-triage/?${params.toString()}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getDashboardProcessIntelligence = async (signal, period = '30d', options = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const params = new URLSearchParams();
    if (workspace) params.append('workspace', workspace);
    if (period) params.append('period', period);
    if (options.fresh) params.append('fresh', '1');
    const url = `${API_BASE}api/dashboard/process-intelligence/?${params.toString()}`;
    return UseAPI(signal, url, 'GET', null);
};

export const refreshDashboardProcessIntelligence = async (signal, period = '7d') => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const params = new URLSearchParams();
    if (workspace) params.append('workspace', workspace);
    const url = `${API_BASE}api/dashboard/process-intelligence/refresh/${params.toString() ? `?${params.toString()}` : ''}`;
    return UseAPI(signal, url, 'POST', {
        period,
    });
};

// ==========================================
// DASHBOARD PHASE 5 - OUTCOME ANALYTICS (App - Token Auth)
// ==========================================

export const logCaseOutcome = async (signal, caseUuid, data) => {
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/outcome/`;
    return UseAPI(signal, url, 'POST', data);
};

export const getDashboardOutcomes = async (signal, period = '30d') => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const params = new URLSearchParams();
    if (workspace) params.append('workspace', workspace);
    if (period) params.append('period', period);
    const url = `${API_BASE}api/dashboard/outcomes/?${params.toString()}`;
    return UseAPI(signal, url, 'GET', null);
};


// ==========================================
// FOLLOW-UP SYSTEM (App - Token Auth)
// ==========================================

export const getCaseFollowups = async (signal, caseUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/followups/${queryString}`;
    return UseAPI(signal, url, 'GET', null);
};


export const createCaseFollowup = async (signal, caseUuid, data) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/followups/${queryString}`;
    return UseAPI(signal, url, 'POST', data);
};

export const generateFollowupAIDraft = async (signal, caseUuid, data = {}) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/followups/ai-draft/${queryString}`;
    return UseAPI(signal, url, 'POST', data);
};

export const getFollowupAISuggestions = async (signal, caseUuid) => {
    const workspace = localStorage.getItem('current_workspace_uuid');
    const queryString = workspace ? `?workspace=${workspace}` : '';
    const url = `${API_BASE}api/inbox/cases/${caseUuid}/followups/ai-suggest/${queryString}`;
    return UseAPI(signal, url, 'POST', {});
};


// ==========================================
// PUBLIC THREAD (No auth — access_token via X-Thread-Access header)
// ==========================================

const _publicFetch = async (url, method, body, accessToken, signal) => {
    const headers = { 'Content-Type': 'application/json', ...getTenantHostHeader() };
    if (accessToken) headers['X-Thread-Access'] = accessToken;
    const opts = { method, headers, signal };
    if (body && method !== 'GET') opts.body = JSON.stringify(body);
    const res = await fetch(url, opts);
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { httpStatusCode: res.status, error: err.error || 'Request failed', ...err };
    }
    return res.json();
};

export const getPublicThread = async (signal, token, accessToken) => {
    const url = `${API_BASE}api/public/thread/${token}/`;
    return _publicFetch(url, 'GET', null, accessToken, signal);
};

export const trackThreadEngagement = async (signal, token, accessToken, data) => {
    const url = `${API_BASE}api/public/thread/${token}/engagement/`;
    return _publicFetch(url, 'PATCH', data, accessToken, signal);
};

export const requestThreadOTP = async (signal, token, accessToken, email) => {
    const url = `${API_BASE}api/public/thread/${token}/request-otp/`;
    return _publicFetch(url, 'POST', { email }, accessToken, signal);
};

export const verifyThreadOTP = async (signal, token, accessToken, code) => {
    const url = `${API_BASE}api/public/thread/${token}/verify-otp/`;
    return _publicFetch(url, 'POST', { code }, accessToken, signal);
};

export const submitThreadReply = async (signal, token, accessToken, data) => {
    const url = `${API_BASE}api/public/thread/${token}/reply/`;
    return _publicFetch(url, 'POST', data, accessToken, signal);
};

export const getPublicThreadUploadURL = async (signal, token, accessToken, data) => {
    const url = `${API_BASE}api/public/thread/${token}/upload-url/`;
    return _publicFetch(url, 'POST', data, accessToken, signal);
};

// ==========================================
// ADMIN INTELLIGENCE (Staff superuser only)
// ==========================================

export const getAdminAnalyticsOverview = async (signal) => {
    const url = `${API_BASE}api/admin/analytics/overview/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAdminAnalyticsUsers = async (signal, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE}api/admin/analytics/users/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAdminAnalyticsStruggle = async (signal) => {
    const url = `${API_BASE}api/admin/analytics/struggle/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAdminAnalyticsUseCases = async (signal) => {
    const url = `${API_BASE}api/admin/analytics/use-cases/`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAdminUsers = async (signal, params = {}) => {
    const queryString = new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== ''))
    ).toString();
    const url = `${API_BASE}api/admin/users/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};

export const getAdminUserResponses = async (signal, userId, params = {}) => {
    const queryString = new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== ''))
    ).toString();
    const url = `${API_BASE}api/admin/users/${userId}/responses/${queryString ? `?${queryString}` : ''}`;
    return UseAPI(signal, url, 'GET', null);
};
